<template>
  <owal-modal-panel
    :header-component="this.headerComponent"
    :header-props="headerProps"
    :bottom-buttons="buttons"
    presentation-style="form"
    @button-click="handleButton"
    @keyup.esc="setTaggingPanelVisibility(true)"
    :tabindex="-1"
    >
    <div class="detection-container">
      <div
        v-for="detection in accessEvent.detections"
        :key="detection.detectionId"
      >
        <detection
          :profile="detection"
          :selected="isDetectionSelected(detection.detectionId)"
          :associated-identity="identityIdAssociatedWithDetectionId(detection.detectionId)"
          :baseImageURL="imageForDetectionID(detection.detectionId)"
          :associatedImageURL="associatedImageURL(detection.detectionId)"
          @click="toggleSelected"
        />
      </div>
    </div>
  </owal-modal-panel>
</template>

<script>
import OwalModalPanel from '@/libraries/owal-ui/panels/OwalModalPanel'
import { updateAccessEventCredentials, rejectConfirmedTags } from '@/libraries/api/credentialMismatch'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ModalTaggingHeader from './ModalTaggingHeader.vue'
import { toggle } from '@/libraries/util/array'
import Detection from '@/components/shared/BaseDetection'
import { imageForDetectionID, urlForIdentityId } from '@/libraries/api/faces'

export default {
  name: 'OwalModalTaggingPanel',
  components: { OwalModalPanel, Detection },
  props: ['credentialUUID'],
  data () {
    return {
      sortedDetectionIds: [],
      activeTenantId: '',
      /** Vue deep watching madness requires that we keep an array alongside a map. */
      confirmedDetectionIds: [], // Detections edited in this session.
      editedDetectionIds: [], // Detections that were tagged when the server sent them down.
      confirmedByDetectionId: {},
      headerComponent: ModalTaggingHeader,
    }
  },
  computed: {
    ...mapGetters({
      accessEvent: 'credentialMismatch/selectedAccessEvent',
      selectedProfile: 'credentialMismatch/selectedProfile',
      cotenants: 'credentialMismatch/selectedIdentityCotenants',
      currentDomainUUID: 'currentDomainUUID',
    }),
    title () {
      return `Click to Select Matching Faces`
    },
    buttons () {
      const buttons = [
        {
          title: 'Save',
          identifier: 'save',
        },
        {
          title: 'Cancel',
          identifier: 'close',
        }]

      return buttons
    },
    selectAndClearAllButtons  () {
      const buttons = [{
        title: 'Select All',
        identifier: 'select-all',
        callback: this.selectAll,
      },
      {
        title: 'Clear All',
        identifier: 'select-none',
        handler: this.selectNone,
      }]

      return buttons
    },
    headerProps () {
      const headerProps = {
        cotenants: this.cotenants,
        badges: this.badges,
      }
      return headerProps
    },
    /**
     * @returns An array containing all of the identity Ids with one or more tags. Identities appear once for each detection, and may be duplicated.
    */
    badgedIdentityIds () {
      const confirmedByDetectionId = this.confirmedByDetectionId
      return this.confirmedDetectionIds.map(detectionId => confirmedByDetectionId[detectionId]).filter(detectionId => detectionId !== undefined)
    },
    badges () {
      const badgeCounts = this.badgedIdentityIds.reduce((prev, curr) => {
        const value = prev[curr] || 0
        prev[curr] = value + 1
        return prev
      }, {})
      return badgeCounts
    },
    confirmedPayload () {
      const accessEventId = this.accessEvent.uuid
      const detectionIds = this.confirmedByDetectionId
      const editedDetectionIds = this.editedDetectionIds
      const payload = Object.keys(detectionIds).map(detectionId => ({
        identity_id: detectionIds[detectionId],
        access_event_uuid: accessEventId,
        detection_id: detectionId,
        rejected: editedDetectionIds.includes(detectionId),
        rejected_for: 'WRONG_PERSON',
      })).filter(tag => {
        return tag.identity_id !== undefined
      })
      return payload
    },
  },
  methods: {
    imageForDetectionID,
    urlForIdentityId,
    ...mapActions({
      loadVerifiedMatchesForIdentityId: 'credentialMismatch/loadVerifiedMatchesForIdentityId',
      loadDetectionsForCredentialUUID: 'credentialMismatch/loadDetectionsForCredentialUUID',
      getUpdatedCredentialUseScore: 'credentialMismatch/getUpdatedCredentialUseScore',
    }),
    ...mapMutations({
      setTaggingPanelVisibility: 'credentialMismatch/setTaggingPanelVisibility',
      setLimited: 'credentialMismatch/setLimited',
    }),
    toggle,
    handleButton: function (identifier) {
      const isIdentitySelectionButton = identifier.startsWith('identityId:')
      if (identifier === 'close') {
        this.setTaggingPanelVisibility(false)
      } else if (identifier === 'save') {
        this.updateConfirmedFaces()
      } else if (identifier === 'select-all') {
        this.selectAll()
      } else if (identifier === 'select-none') {
        this.selectNone()
      } else if (isIdentitySelectionButton) {
        const selectedIdentityId = identifier.replace('identityId:', '')
        this.activeTenantId = selectedIdentityId
      }
    },
    isDetectionSelected (detectionId) {
      const selected = this.confirmedDetectionIds.includes(detectionId)
      return selected
    },
    /**
     * Ensures that a detection is selected correctly.
     */
    toggleSelected (detectionId) {
      const tenantId = this.activeTenantId
      let confirmed = this.confirmedByDetectionId[detectionId]

      this.confirmedDetectionIds = this.confirmedDetectionIds.filter(d => d !== detectionId)

      if (confirmed !== tenantId) {
        this.confirmedByDetectionId[detectionId] = tenantId
        this.confirmedDetectionIds.push(detectionId)
      } else {
        this.confirmedByDetectionId[detectionId] = undefined
      }
    },
    selectAll () {
      this.confirmed = this.accessEvent.detections.map(d => d.detectionId)
    },
    selectNone () {

    },
    updateConfirmedFaces () {
      let removeRejected = []
      let tagsForRejecting = []
      if (this.accessEvent.confirmedDetections) {
        removeRejected = this.accessEvent.confirmedDetections.filter(detection => !(detection.rejected === true && detection.rejected_for === 'WRONG_PERSON'))
        tagsForRejecting = removeRejected.filter(detection => !this.confirmedDetectionIds.includes(detection.detection_id))
      }
      updateAccessEventCredentials(this.confirmedPayload, this.currentDomainUUID).then(res => {
        if (!res.data.ok) {
          throw new Error(`Error: ${JSON.stringify(res)}`)
        }
      }).then(res => {
        if (tagsForRejecting.length) {
          const payload = tagsForRejecting.map(tag => {
            return { ...tag, rejected: true, rejected_for: 'WRONG_PERSON', access_event_uuid: this.accessEvent.uuid }
          })
          rejectConfirmedTags(payload, this.currentDomainUUID).then(res => {
            if (res.data.ok) {
              // console.log('rejected ok')
            }
          }).catch(error => {
            console.log('error', error)
          })
        }
      })
        .then(function (res) {
          this.setTaggingPanelVisibility(false)
          this.setLimited(false)
          this.loadDetectionsForCredentialUUID(this.credentialUUID)
          this.loadVerifiedMatchesForIdentityId(this.selectedProfile.identityId)
          this.getUpdatedCredentialUseScore(this.selectedProfile.identityId)
        }.bind(this, tagsForRejecting))
        .catch(error => {
          // alert(`Error: `, error)
          console.log('error: ', error)
        })
    },
    associatedImageURL (detectionId) {
      const identityId = this.confirmedByDetectionId[detectionId]
      return identityId ? urlForIdentityId(identityId) : undefined
    },
    identityIdAssociatedWithDetectionId (detectionId) {
      return this.confirmedByDetectionId[detectionId]
    },
  },
  mounted () {
    this.activeTenantId = this.selectedProfile.identityId
    this.sortedDetectionIds = this.accessEvent.detections

    if (this.accessEvent.confirmedDetections) {
      const removeRejected = this.accessEvent.confirmedDetections.filter(detection => !(detection.rejected === true && detection.rejected_for === 'WRONG_PERSON'))
      this.confirmedByDetectionId = removeRejected.reduce((confirmedDetectionsDict, detection) => {
        confirmedDetectionsDict[detection.detection_id] = detection.identity_id
        return confirmedDetectionsDict
      }, {})
    }

    this.confirmedDetectionIds = Object.keys(this.confirmedByDetectionId)
    this.editedDetectionIds = Object.keys(this.confirmedByDetectionId)
  },
}
</script>

<style scoped>
.detection-container {
  display: grid;
  grid-template-columns: 0.9fr 0.9fr 0.9fr 0.9fr;
  column-gap: 0.133fr;
  justify-items: center;
}

.selected {
  background: red;
  border: 4px solid forestgreen;
  transition: border 300ms ease;
  box-sizing: border-box;
}

.owal.faces.list.avatar {
  background-color: #eff2f7;
  object-fit: cover;
  height: 13rem;
  width: 13rem;
  border-radius: 1rem;
  margin: 0;
  transition: border 300ms ease;
}

.owal-confidence-red {
  color: #c0ccda;
  background: rgba(208, 2, 27, 1);
}

.owal-confidence-yellow {
  color: #475669;
  background: rgba(255, 243, 108, 1);
}

.owal-confidence-green {
  color: #475669;
  background: rgba(171, 213, 124, 1);
}

.owal-tagging-panel__circle_checked,
.owal-tagging-panel__circle_unchecked {
  height: 2rem;
  width: 2rem;
  border-radius: 1rem;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  align-self: flex-end;
  position: relative;
  margin-top: -2.5rem;
  margin-bottom: 2.5rem;
  margin-right: 1rem;
  margin-left: auto;
  transition: background 300ms ease, color 300ms ease;
}

.owal-tagging-panel__circle_checked {
  border: 1px solid #eff2f7;
  background: forestgreen;
  color: #fff;
}

.owal-tagging-panel__circle_unchecked {
  background: #475669;
  color: #c0ccda;
  border: 1px solid #eff2f7;
}
</style>
